// Radvis sparning av listor i ett Firestore-dokument.
//
// Varje rad har ett stabilt id. diffRows räknar fram vad som lagts till,
// ändrats, tagits bort eller flyttats mellan två lägen av en lista, och
// applyRows tillämpar den ändringen på listans aktuella läge i databasen
// (läst i en transaktion). Andras samtidiga ändringar i andra rader blir kvar.
// Används av konsultprofilen (profileSync.js) och uppdragsbudgeten
// (modules/uppdrag/budgetSync.js).

export const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Vad som skiljer listan `after` från `before`, eller null om inget.
 *   list       listans namn, ger kvittensnycklarna
 *   radKvittens true = ändrade fält kvitteras på raden (`list.id`),
 *               annars per fält (`list.id.fält`)
 * → { change: { removed, added, updates, order }, keys }
 */
export function diffRows(list, before, after, { radKvittens = false } = {}) {
  const beforeById = new Map(before.filter(r => r.id).map(r => [r.id, r]));
  const afterIds = new Set(after.map(r => r.id));
  const removed = before.filter(r => r.id && !afterIds.has(r.id)).map(r => r.id);
  const added = [];
  const updates = {};
  const keys = [];
  const rowKey = id => `${list}.${id}`;
  const fieldKey = (id, f) => (radKvittens ? rowKey(id) : `${list}.${id}.${f}`);

  after.forEach((row, i) => {
    const old = beforeById.get(row.id);
    if (!old) {
      added.push({ row, after: i === 0 ? null : after[i - 1].id });
      keys.push(rowKey(row.id));
      return;
    }
    const changed = {};
    new Set([...Object.keys(old), ...Object.keys(row)]).forEach(k => {
      if (k !== 'id' && !same(old[k], row[k])) {
        changed[k] = row[k] === undefined ? null : row[k];
        keys.push(fieldKey(row.id, k));
      }
    });
    if (Object.keys(changed).length) updates[row.id] = changed;
  });

  const keptBefore = before.filter(r => afterIds.has(r.id)).map(r => r.id);
  const keptAfter = after.filter(r => beforeById.has(r.id)).map(r => r.id);
  let order = null;
  if (!same(keptBefore, keptAfter)) {
    order = keptAfter;
    keptAfter.forEach((id, i) => { if (keptBefore[i] !== id) keys.push(rowKey(id)); });
  }
  if (removed.length) keys.push(`${list}.removed`);

  if (!removed.length && !added.length && !Object.keys(updates).length && !order) return null;
  return { change: { removed, added, updates, order }, keys };
}

/** Tillämpar en radändring på listans aktuella läge i databasen. */
export function applyRows(remote, change) {
  let rows = (remote || []).filter(r => !(r?.id && change.removed.includes(r.id)));
  rows = rows.map(r => (r?.id && change.updates[r.id] ? { ...r, ...change.updates[r.id] } : r));
  change.added.forEach(({ row, after }) => {
    if (rows.some(r => r?.id === row.id)) return; // redan sparad (omförsök)
    const prev = after === null ? -1 : rows.findIndex(r => r?.id === after);
    const at = after === null ? 0 : prev >= 0 ? prev + 1 : rows.length;
    rows.splice(at, 0, row);
  });
  if (change.order) {
    // Byter bara plats på de rader vi känner till; andras nya rader står kvar
    const pos = new Map(change.order.map((id, i) => [id, i]));
    const slots = rows.map((r, i) => (pos.has(r?.id) ? i : -1)).filter(i => i >= 0);
    const sorted = slots.map(i => rows[i]).sort((x, y) => pos.get(x.id) - pos.get(y.id));
    slots.forEach((slot, k) => { rows[slot] = sorted[k]; });
  }
  return rows;
}
